import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { ApiError } from '@/api/client';
import { api } from '@/api/client';
import { asApiError } from '@/api/errors';
import { squareKey } from '@/api/grid';
import type {
  ClaimGameResult,
  ClaimResult,
  GameSummary,
  Coordinate,
  CreateGameRequest,
  EmptySquarePolicy,
  Game,
  GameDetail,
  Player,
  Result,
  UpdateGameRequest,
} from '@/api/types';

/**
 * Games the app is currently looking at. Views go through here rather than calling `api`
 * themselves — see apps/web/docs/ARCHITECTURE.md.
 */
export const useGamesStore = defineStore('games', () => {
  const current = ref<GameDetail | null>(null);
  /** Every game this subject has touched, newest first — what /games renders. */
  const mine = ref<GameSummary[]>([]);
  const listing = ref(false);
  const loading = ref(false);
  const saving = ref(false);
  const error = ref<ApiError | null>(null);

  /**
   * Rethrows on purpose. A 403 ACCOUNT_REQUIRED means "send them to sign in", a 400 means "mark
   * the offending fields", a 429 means "say how long" — only the view can choose between those,
   * so swallowing the error here would take the choice away.
   */
  async function create(payload: CreateGameRequest): Promise<Game> {
    saving.value = true;
    try {
      return await api.post<Game>('/games', payload);
    } finally {
      // In `finally`, so a failure re-enables the submit button rather than stranding the host
      // on a form they can no longer send.
      saving.value = false;
    }
  }

  /**
   * Captures rather than throws: for the game screen, rendering the failure *is* the job — a
   * missing code and a dropped connection are both states with their own copy.
   */
  async function load(code: string): Promise<void> {
    /*
     * A *different* game is dropped before the read starts, and this is the whole reason the
     * views can say `loading && !current`: opening one game from /games while another was on the
     * store rendered the old one — name, grid and all — for the length of a round trip before it
     * was replaced. Keeping the same game is deliberate, and is what makes the error screen's
     * Try again, and the reload after a claim, a top-up rather than a blank page.
     */
    if (current.value && current.value.game.code !== code) current.value = null;

    loading.value = true;
    error.value = null;
    try {
      current.value = await api.get<GameDetail>(`/games/${code}`);
    } catch (caught) {
      current.value = null;
      error.value = asApiError(caught);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Every game the caller hosts or plays, newest first.
   *
   * Captures rather than throws, like `load`: on this screen rendering the failure *is* the job.
   * The list is left alone on a failure so a dropped connection does not blank a usable page.
   */
  async function listMine(): Promise<void> {
    listing.value = true;
    error.value = null;
    try {
      const result = await api.get<{ games: GameSummary[] }>('/games');
      mine.value = result.games;
    } catch (caught) {
      error.value = asApiError(caught);
    } finally {
      listing.value = false;
    }
  }

  /**
   * "I played that one as a guest, on the other phone." Moves one game's history onto this
   * account — the squares, the player row, and the game itself if the guest was hosting it.
   *
   * Rethrows, like every other action here. PLAYER_NOT_FOUND wants the name field marked,
   * NOT_CLAIMABLE and AMBIGUOUS_PLAYER each need their own sentence, and a 429 needs a wait —
   * only the view can choose between those.
   *
   * Named for the game rather than for squares: `claim` above is the player picking squares, and
   * the two are different verbs that happen to share a word.
   */
  async function claimGame(code: string, displayName: string): Promise<ClaimGameResult> {
    saving.value = true;
    try {
      return await api.post<ClaimGameResult>(`/games/${code}/claim`, { displayName });
    } finally {
      saving.value = false;
    }
  }

  /**
   * Forgets everything about the games this browser was looking at.
   *
   * Called on sign-out: a shared laptop must not still hold the names of somebody else's pools
   * when the next person arrives, and /games fetches on mount, so there must be nothing to flash
   * before that lands.
   */
  function clear(): void {
    current.value = null;
    mine.value = [];
    error.value = null;
  }

  /**
   * A background top-up of the same game — what the polling timer and the Refresh button call.
   *
   * Deliberately *not* `load`, in two ways. It never raises `loading`, because that swaps the
   * screen for an outline and doing so every ten seconds would be a flicker rather than an
   * update. And a failure is swallowed: a dropped connection mid-poll must leave a usable grid on
   * screen instead of replacing it with an error page. The caller learns from the boolean, which
   * is also what backs the timer off.
   *
   * See docs/decisions/0009-polling-not-websockets.md.
   */
  async function refresh(code: string): Promise<boolean> {
    try {
      const detail = await api.get<GameDetail>(`/games/${code}`);

      /*
       * The other half of `load` dropping a stale game: a tick can be in flight when somebody
       * leaves a screen, and it resolves after the timer that started it has gone. Holding a
       * *different* game means the reader has moved on, and landing this one on them would be
       * the same flash from the other direction.
       *
       * Holding nothing is deliberately not that case — it is a failed load, and a poll that
       * works is how such a screen recovers on its own.
       */
      if (current.value && current.value.game.code !== code) return true;

      current.value = detail;
      // A read that works clears a stale failure, so a screen that failed to load recovers on
      // its own once the connection comes back.
      error.value = null;
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Joining is idempotent, so the SPA calls it on every arrival at a game rather than tracking
   * whether this browser has been here before. A rejoin renames; it never resets what somebody
   * holds. See the API's docs/API.md.
   *
   * Every action below rethrows. A 409 SQUARE_TAKEN needs the grid refreshed and the conflicting
   * squares named, a GAME_NOT_OPEN needs a different screen, a NOT_JOINED needs the join form —
   * only the view can choose between those.
   */
  async function join(code: string, displayName: string): Promise<Player> {
    saving.value = true;
    try {
      return await api.post<Player>(`/games/${code}/join`, { displayName });
    } finally {
      saving.value = false;
    }
  }

  async function claim(code: string, squares: Coordinate[]): Promise<ClaimResult> {
    saving.value = true;
    try {
      return await api.post<ClaimResult>(`/games/${code}/squares`, { squares });
    } finally {
      // In `finally`: a failed claim must re-enable the button rather than strand the player on
      // a grid they can no longer act on.
      saving.value = false;
    }
  }

  async function autoPick(code: string, count: number): Promise<ClaimResult> {
    saving.value = true;
    try {
      return await api.post<ClaimResult>(`/games/${code}/squares/auto`, { count });
    } finally {
      saving.value = false;
    }
  }

  async function release(code: string, square: Coordinate): Promise<void> {
    saving.value = true;
    try {
      // The coordinate is in the path, built by the same helper the grid keys cells with, so the
      // two cannot disagree about the order of col and row.
      await api.delete(`/games/${code}/squares/${squareKey(square.col, square.row)}`);
    } finally {
      saving.value = false;
    }
  }

  /*
   * The host's end of the lifecycle. Each one rethrows like the player actions above: a 409
   * GAME_NOT_OPEN after another tab locked, a GAME_NOT_LOCKED, an ALREADY_COMPLETED — the view
   * is the only thing that knows which screen those belong on.
   */

  /**
   * Locking answers with the whole game, and under DISTRIBUTE_FREE that response is the *only*
   * place the freshly dealt squares appear without another read — so it is kept rather than
   * discarded, unlike the actions that deliberately re-read.
   */
  async function lock(code: string, emptySquarePolicy: EmptySquarePolicy): Promise<GameDetail> {
    saving.value = true;
    try {
      const detail = await api.post<GameDetail>(`/games/${code}/lock`, { emptySquarePolicy });
      current.value = detail;
      return detail;
    } finally {
      saving.value = false;
    }
  }

  async function unlock(code: string): Promise<Game> {
    saving.value = true;
    try {
      return await api.post<Game>(`/games/${code}/unlock`);
    } finally {
      saving.value = false;
    }
  }

  async function score(
    code: string,
    payload: { teamAScore: number; teamBScore: number },
  ): Promise<Result> {
    saving.value = true;
    try {
      return await api.post<Result>(`/games/${code}/score`, payload);
    } finally {
      saving.value = false;
    }
  }

  async function cancel(code: string): Promise<Game> {
    saving.value = true;
    try {
      return await api.post<Game>(`/games/${code}/cancel`);
    } finally {
      saving.value = false;
    }
  }

  /**
   * Removes a player, handing their squares to somebody else first when a destination is given —
   * which is the fix for one person who joined on two devices. Answers 204, so there is nothing
   * to keep: the view re-reads, the same as every host action but locking.
   */
  async function removePlayer(
    code: string,
    subjectId: string,
    transferSquaresTo?: string,
  ): Promise<void> {
    saving.value = true;
    try {
      const destination =
        transferSquaresTo === undefined
          ? ''
          : `?transferSquaresTo=${encodeURIComponent(transferSquaresTo)}`;
      await api.delete(`/games/${code}/players/${subjectId}${destination}`);
    } finally {
      saving.value = false;
    }
  }

  /**
   * A settings change. Answers with the whole game, which the current detail keeps — a re-read
   * would throw away the authoritative answer and pay for a second request to get it back.
   *
   * Only `allowHostSuperPowers` goes through here today; the rest of the settings have no screen
   * yet. The route takes them all, so this stays a patch rather than a single-purpose call.
   */
  async function updateSettings(code: string, patch: UpdateGameRequest): Promise<Game> {
    saving.value = true;
    try {
      const game = await api.patch<Game>(`/games/${code}`, patch);
      if (current.value?.game.code === game.code) current.value = { ...current.value, game };
      return game;
    } finally {
      saving.value = false;
    }
  }

  /** Bookkeeping, not a payment. This app moves no money — see the API's ADR-0005. */
  async function setPaid(code: string, subjectId: string, paid: boolean): Promise<Player> {
    saving.value = true;
    try {
      return await api.patch<Player>(`/games/${code}/players/${subjectId}`, { paid });
    } finally {
      saving.value = false;
    }
  }

  /**
   * Local play: somebody at the table who never opened the app. Returns the row rather than only
   * re-reading the game, because the screen selects the person it has just added and the subject
   * id it needs to name them is minted by the server.
   */
  async function createLocalPlayer(code: string, displayName: string): Promise<Player> {
    saving.value = true;
    try {
      return await api.post<Player>(`/games/${code}/players`, { displayName });
    } finally {
      saving.value = false;
    }
  }

  /**
   * The host override: up to 25 squares into one player's hands, in one request. Two cases behind
   * it and the API decides per square — moving one somebody holds adds nothing to the pot, handing
   * out an empty one buys it in their name.
   *
   * `subjectId: null` is the third: nobody. The squares come off the grid, off their holders'
   * ledgers and out of the pot — the undo for somebody who tapped the wrong square and has since
   * put their phone down. Null rather than a second method because it is one route and one panel:
   * the host taps squares and then says where they end up, and nobody is one of the answers.
   *
   * One request rather than one per square, because it is all-or-nothing: a host who meant to hand
   * over ten must never find they handed over nine. Answers 204, so there is nothing to keep — the
   * view re-reads, like every host action but locking.
   */
  async function assignSquares(
    code: string,
    subjectId: string | null,
    squares: Coordinate[],
  ): Promise<void> {
    saving.value = true;
    try {
      await api.patch(`/games/${code}/squares`, { subjectId, squares });
    } finally {
      saving.value = false;
    }
  }

  return {
    current,
    mine,
    listing,
    loading,
    saving,
    error,
    create,
    load,
    listMine,
    claimGame,
    clear,
    refresh,
    join,
    claim,
    autoPick,
    release,
    lock,
    unlock,
    score,
    cancel,
    setPaid,
    removePlayer,
    updateSettings,
    createLocalPlayer,
    assignSquares,
  };
});
